import React, { useMemo } from "react";

import cx from "classnames";

import { AnimationProperty } from "../components/animations";
import { ButtonMobileStyleEditor, ButtonStyleEditor } from "../widgets/button";
import { ShapeStyleEditor } from "../widgets/shape";
import { LineStyleEditor } from "../widgets/line";
import { RectangleStyleEditor } from "../widgets/rectangle";
import { BorderPropertyDialog } from "../widgets/image";
import { LiveElementCreationProperty } from "../components/liveElements";
import { BrandComponentProperty } from "../components/brandElements";
import {
  BUTTON,
  IMAGE,
  LINE,
  RECTANGLE,
  SHAPE,
  TEXT,
  VIDEO,
} from "constants/widgetTypes";
import { ImageCropper } from "../widgets/image/crop";

export const actionsList = [
  "liveElement",
  "brandMedia",
  "animation",
  "shape",
  "rectangle",
  "line",
  "borders",
  "customizeButton",
  "mobileCustomizeButton",
  "cropImage",
];

// 1. Create registry
const registry = new Map();

const registerViewComponent = (view, Component) => {
  registry.set(view, Component);
};

const getViewComponent = (view) => {
  return registry.get(view) || null;
};

const views = {
  animation: AnimationProperty,
  customizeButton: ButtonStyleEditor,
  mobileCustomizeButton: ButtonMobileStyleEditor,
  shape: ShapeStyleEditor,
  line: LineStyleEditor,
  rectangle: RectangleStyleEditor,
  borders: BorderPropertyDialog,
  liveElement: LiveElementCreationProperty,
  brandMedia: BrandComponentProperty,
  cropImage: ImageCropper,
};

// Step 2: Register all view components
Object.entries(views).forEach(([key, Component]) => {
  registerViewComponent(key, Component);
});

export const GroupPropertyPanel = ({
  view,
  selectedWidgets,
  editorSession,
  editorContext,
  elements,
  setView,
  onCrop,
  crop,
}) => {
  const Component = getViewComponent(view);

  const commonProps = useMemo(
    () => ({
      // Common props
      ids: selectedWidgets.map((w) => w.uid),
      widgets: selectedWidgets,
      elements,
      editorSession,
      editorContext,
      onCancel: () => setView(null),
      type: 'SECTIONS',

      // Image crop props
      images: selectedWidgets.map((w) => w.content),
      show: true,
      crop,
      onCrop,
    }),
    [selectedWidgets, elements, editorSession, editorContext]
  );

  if (!Component) return null;

  return <Component {...commonProps} />;
};

// --- Group Widget Actions ---
const createCustomizeAction = (tooltip, mode, styles) => ({
  label: <i className={cx("cleverstory-customize")} />,
  tooltip,
  className: styles.text,
  onClick: (handleEditMode) => () => handleEditMode(mode),
});

const animatableTypes = [TEXT, BUTTON, VIDEO, IMAGE, SHAPE, RECTANGLE, LINE];

export const GroupWidgetActions = ({ breakpoint, styles }) => ({
  text: {
    show: ({ widgets }) => widgets.every((w) => w.type === TEXT),
    actions: [
      {
        label: "Format text",
        className: styles.text,
        onClick: (handleEditMode) => () => handleEditMode("text"),
      },
    ],
  },
  liveElement: {
    show: ({ canCreateLiveElement }) => !!canCreateLiveElement,
    actions: [
      {
        label: "Make Live Element",
        className: styles.text,
        onClick: (handleEditMode) => () => handleEditMode("liveElement"),
      },
    ],
  },
  group: {
    show: ({ canGroupWidgets }) => !!canGroupWidgets,
    actions: [
      {
        label: "Group",
        className: cx(styles.text, styles.groupTextButton),
        onClick: (handleEditMode) => () => handleEditMode("group"),
      },
    ],
  },
  makeFrame: {
    show: ({ makeFrame }) => !!makeFrame && breakpoint === 'desktop',
    actions: [
      {
        label: "Make Frame",
        tooltip: 'Split segment: selection becomes a new frame',
        className: styles.text,
        onClick: (handleEditMode) => () => handleEditMode("makeFrame"),
      },
    ],
  },
  button: {
    show: ({ widgets }) => widgets.every((w) => w.type === BUTTON),
    actions: [
      createCustomizeAction(
        "Customize",
        breakpoint === "desktop" ? "customizeButton" : "mobileCustomizeButton",
        styles
      ),
    ],
  },
  image: {
    show: ({ widgets }) =>
      widgets.every((w) => w.type === IMAGE),
    actions: [
      {
        label: <i className={cx("cleverstory-corner")} />,
        tooltip: "Border",
        className: styles.text,
        onClick: (handleEditMode) => () => handleEditMode("borders"),
      },
      {
        label: <i className={cx("cleverstory-crop")} />,
        tooltip: "Crop",
        className: styles.text,
        onClick: (handleEditMode) => () => handleEditMode("cropImage"),
      },
    ],
  },
  line: {
    show: ({ widgets }) => widgets.every((w) => w.type === LINE) && breakpoint === 'desktop',
    actions: [createCustomizeAction("Customize", "line", styles)],
  },
  shape: {
    show: ({ widgets }) => widgets.every((w) => w.type === SHAPE) && breakpoint === 'desktop',
    actions: [createCustomizeAction("Customize", "shape", styles)],
  },
  rectangle: {
    show: ({ widgets }) => widgets.every((w) => w.type === RECTANGLE) && breakpoint === 'desktop',
    actions: [createCustomizeAction("Customize", "rectangle", styles)],
  },
  animation: {
    show: ({ widgets }) =>
      widgets.every((w) => animatableTypes.includes(w.type)),
    actions: [
      {
        label: <i className={cx("cleverstory-animations")} />,
        tooltip: "Animation",
        className: styles.iconButton,
        onClick: (handleEditMode) => () => handleEditMode("animation"),
      },
    ],
  },
  brandKit: {
    show: ({ canBrandKitWidgets, role }) => !!canBrandKitWidgets && role,
    actions: [
      {
        label: <i className={cx("cleverstory-brandmedia")} />,
        tooltip: "Add to Brand Kit",
        className: styles.iconButton,
        onClick: (handleEditMode) => () => handleEditMode("brandMedia"),
      },
    ],
  },
});

// --- Optional Utility ---
export const getAvailableActions = (actionsFn, styles, props, handleEditMode) =>
  Object.values(actionsFn(styles))
    .filter((group) => group.show(props))
    .flatMap((group) =>
      group.actions.map((action) => ({
        ...action,
        onClick: action.onClick(handleEditMode),
      }))
    );
